import { ReactNode, useContext } from 'react'
import { createPortal } from 'react-dom'

import { PortalContext } from 'core-components/library/popup'
import { INotificationPopup } from 'core-components/library/popup/notification/Notification'

import { PopupController } from '../wrapper/Controller'

import styles from './NotificationPortal.module.scss'

const NOTIFICATION_DOM_ID = 'popup-notification-root'

interface IPopupPortal {
  children: ReactNode
  verticalPosition?: INotificationPopup['verticalPosition']
  horizontalPosition?: INotificationPopup['horizontalPosition']
}

const createRoot = ({
  popupId,
  horizontalPosition,
  verticalPosition,
  container,
}: {
  popupId: string
  verticalPosition: IPopupPortal['verticalPosition']
  horizontalPosition: IPopupPortal['horizontalPosition']
  container: HTMLElement
}) => {
  const root = document.createElement('div')
  root.setAttribute('id', popupId)
  root.classList.add(styles.root)
  if (verticalPosition) {
    root.classList.add(styles[verticalPosition])
  }
  if (horizontalPosition) {
    root.classList.add(styles[horizontalPosition])
  }
  container.prepend(root)
  return root
}

const generateId = (
  verticalPosition: IPopupPortal['verticalPosition'],
  horizontalPosition: IPopupPortal['horizontalPosition']
) => {
  return `${NOTIFICATION_DOM_ID}-${verticalPosition}-${horizontalPosition}`
}

export const Portal = ({
  children,
  verticalPosition = 'bottom',
  horizontalPosition = 'right',
}: IPopupPortal) => {
  const { containerParent } = useContext(PortalContext)
  const id = generateId(verticalPosition, horizontalPosition)
  let element = document.getElementById(id) as HTMLDivElement
  if (!element) {
    element = createRoot({
      popupId: id,
      verticalPosition,
      horizontalPosition,
      container: containerParent || document.body,
    })
  }

  return createPortal(<PopupController modal={false}>{children}</PopupController>, element)
}
